import qs from 'qs';
import cookie from 'cookie';
import actionTypes from 'Modules/auth/actions/types';
import { loginUserFailure, loginUserStart, loginUserSuccess } from 'Modules/auth/actions';
import { getShouldAskForBenefits } from 'Modules/currentUser/services';
import { browser } from '@openclassrooms/utils';
import {
  removeCachedCurrentUser,
  setCachedCurrentUser,
} from '@openclassrooms/data-connect-web/users';
import { ajaxPost } from 'Modules/ajax';
import { getActiveLang } from '@openclassrooms/translations';
import { getPathName, isFromHomePage, ROUTES } from '@openclassrooms/routing';

import sdk from 'Modules/api/services/sdk';
import scopes from 'Modules/api/constants/scopes';

import { SIGN_IN_SOURCE } from '../constants/constants';

const EXCLUDE_FROM_WELCOME_REDIRECTION = /subscribe|partners|application|mp/;

export function buildRedirectLink(urls) {
  if (urls.length === 1) {
    return urls[0];
  }

  const targetPath = buildRedirectLink(urls.slice(1));

  const url = new URL(urls[0], document.location.origin);
  url.searchParams.append('redirectLink', targetPath);
  return url.pathname + url.search;
}

export function getConditionalRedirectLink(urls) {
  const filteredUrls = Object.keys(urls).filter((u) => urls[u]);

  return buildRedirectLink(filteredUrls);
}

const socialLoginMiddleware = (store) => (next) => async (action) => {
  const { type, payload } = action;

  if (type === actionTypes.POST_SOCIAL_LOGIN_TRIGGER) {
    store.dispatch(loginUserStart(SIGN_IN_SOURCE.SOCIAL));

    try {
      const isAppleLogin = payload.type === 'apple';
      const baseUrl = `/${getActiveLang()}/login_check_${payload.type}`;
      let url;
      switch (payload.type) {
        case 'apple':
          url = baseUrl;
          break;
        case 'google':
          url = `${baseUrl}?idToken=${payload.accessToken}`;
          break;
        default:
          url = `${baseUrl}?accessToken=${payload.accessToken}`;
      }

      const csrfTokenFormData = {
        state: payload.csrfToken,
      };

      const appleExtraParameters = {
        body: qs.stringify({
          ...csrfTokenFormData,
          code: payload.authorization?.code,
          // eslint-disable-next-line camelcase
          id_token: payload.authorization?.id_token,
          user: JSON.stringify(payload.user),
        }),
        headers: {
          'content-type': 'application/x-www-form-urlencoded',
        },
      };

      const login = await ajaxPost(url, {
        ...(isAppleLogin
          ? appleExtraParameters
          : {
              formData: csrfTokenFormData,
            }),
        noLog: ({ status }) => status === 401,
      });

      if (login.status === 200) {
        await removeCachedCurrentUser();

        const isRegister = login.headers.get('x-external-action') === 'created-from-external';
        store.dispatch(loginUserSuccess());

        const cookies = cookie.parse(browser.document.cookie);
        const COOKIES_ACCESS_TOKEN_KEY = 'access_token';
        const COOKIES_EXPIRES_IN_KEY = 'access_token_expires_in';

        sdk.authClient.setOauth2Tokens({
          accessToken: cookies[COOKIES_ACCESS_TOKEN_KEY],
          expiresIn: cookies[COOKIES_EXPIRES_IN_KEY],
          grantType: 'implicit',
          scopes: scopes.CONNECTED,
        });

        const { body: currentUser } = await sdk.client.helpers.user.me.get();

        await setCachedCurrentUser(currentUser);

        const userId = currentUser.id;
        const shouldAskForBenefits = await getShouldAskForBenefits(userId);

        const { body } = await sdk.client.helpers.user.users.getCommunicationSettings(userId);

        const finalDestinationLink = isFromHomePage(payload?.redirectLink)
          ? getPathName(ROUTES.DASHBOARD.INDEX)
          : payload?.redirectLink;

        const welcomePageLink = getPathName(ROUTES.WELCOME.INDEX);
        const askForBenefitsLink = `${welcomePageLink}?steps=benefits`;

        const checkDetailsLink = getPathName(ROUTES.AUTH.CHECK_DETAILS);

        const shouldGoToCheckDetails = Boolean(
          body.find(
            ({ type: t, updatedAt }) => t === 'accept to receive newsletter' && updatedAt === null
          )
        );

        const shouldGoToWelcome =
          !EXCLUDE_FROM_WELCOME_REDIRECTION.test(payload?.redirectLink) && isRegister;

        browser.window.location.href = getConditionalRedirectLink({
          [checkDetailsLink]: shouldGoToCheckDetails,
          [welcomePageLink]: shouldGoToWelcome,
          [askForBenefitsLink]: !shouldGoToWelcome && shouldAskForBenefits,
          [finalDestinationLink]: true,
        });
      } else if (login.data && login.data.errors) {
        store.dispatch(loginUserFailure(login.data.errors));
      } else {
        store.dispatch(loginUserFailure({}));
      }
    } catch (error) {
      store.dispatch(loginUserFailure(error));
    }
  }

  return next(action);
};

export default socialLoginMiddleware;
